import React, { useState } from 'react';
import { toast } from 'sonner';
import { httpsCallable } from 'firebase/functions';
import { doc, writeBatch, collection } from '@firebase/firestore';
import { captureException as sentryCaptureException } from '@sentry/react';

import { useAuth } from '../../../hooks';
import { createEmailObj } from '../../../functions';
import { db, functions } from '../../../firebase-config';
import { ManualVerificationPresentation } from './manual-verification-presentation';
import { SUPPORT_EMAIL_ADDRESS, EMAIL_TEMPLATES, getPatientLink } from '../../../utils';

/**
 * @type {import('firebase/functions').HttpsCallable<{ patientId: string, verificationMethod: 'DIGITAL_ID' | 'MANUAL' }, { success: boolean, message: string, alreadyPending: boolean }>}
 */
const updateIdVerificationIntakeStatusFunction = httpsCallable(functions, 'updatePatientIdVerificationIntakeStatus_v2');

/**
 * @param {object} props
 * @param {() => void} props.onSuccess
 * @param {string} props.patientId
 * @returns {JSX.Element}
 */
export const IdentityVerification = ({ onSuccess, patientId }) => {
  const { user } = useAuth();
  const [frontUrl, setFrontUrl] = useState('');
  const [backUrl, setBackUrl] = useState('');

  const handleNext = async () => {
    if (!user?.uid) {
      toast.error('An error occurred', { description: 'Please try again later.' });

      return;
    }

    try {
      const batch = writeBatch(db);
      const mailRef = doc(collection(db, 'mail'));
      const patientRef = doc(db, 'patients', user.uid);
      const activityRef = doc(collection(db, 'patients', user.uid, 'activity'));

      batch.set(
        mailRef,
        createEmailObj({
          to: SUPPORT_EMAIL_ADDRESS,
          template: EMAIL_TEMPLATES.IDENTITY_DOCUMENTS_UPLOADED,
          data: { patientEmail: user.email, patientLink: getPatientLink(patientId, true) },
        }),
      );

      batch.update(patientRef, { identification: 'pending' });

      batch.set(activityRef, {
        author: 'System',
        text: `The patient requested to be manually verified.`,
        createdAt: Date.now(),
        generalData: true,
      });

      await batch.commit();

      await updateIdVerificationIntakeStatusFunction({
        patientId,
        verificationMethod: 'MANUAL',
      });

      onSuccess();
    } catch (error) {
      if (import.meta.env.DEV) {
        toast.error(error.message);
      }

      sentryCaptureException(error, {
        tags: { issueIn: 'IdentityVerification.handleNext' },
        user: { id: user?.uid },
        extra: { patientId, frontUrl, backUrl },
      });
    }
  };

  return (
    <ManualVerificationPresentation
      handleNext={handleNext}
      frontUrl={frontUrl}
      setFrontUrl={setFrontUrl}
      backUrl={backUrl}
      setBackUrl={setBackUrl}
      patientId={patientId}
    />
  );
};
